import { useEffect, useMemo, useState } from 'react';

import { useAuth } from 'services/auth/authContext';
import {
  calculateCashbackEarnAmount,
  calculateMaxCashbackRedeem,
  getCashbackWalletByEmail,
  getGrowlersCashbackConfig,
} from 'services/firebase/growlersCashbackService';
import { roundCurrency } from 'utils/currency';
import {
  DEFAULT_GROWLERS_CASHBACK_CONFIG,
  type GrowlersCashbackConfig,
} from 'utils/types/GrowlersCashback';

type UseGrowlersCashbackResult = {
  loading: boolean;
  config: GrowlersCashbackConfig;
  balance: number;
  maxRedeem: number;
  canRedeem: boolean;
  accountEmail: string | null;
  /** Cashback que esta compra deve creditar (sobre o subtotal dos itens). */
  earnAmount: number;
  earnPercent: number;
};

export const useGrowlersCashback = (
  merchandiseSubtotal: number,
): UseGrowlersCashbackResult => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<GrowlersCashbackConfig>(
    DEFAULT_GROWLERS_CASHBACK_CONFIG,
  );
  const [balance, setBalance] = useState(0);

  const canUseAccount = Boolean(
    currentUser && !currentUser.isAnonymous && currentUser.email,
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const nextConfig = await getGrowlersCashbackConfig();
        if (cancelled) {
          return;
        }
        setConfig(nextConfig);

        if (!canUseAccount || !currentUser?.email) {
          setBalance(0);
          return;
        }

        const wallet = await getCashbackWalletByEmail(currentUser.email);
        if (cancelled) {
          return;
        }

        if (
          wallet &&
          (!wallet.uid || wallet.uid === currentUser.uid) &&
          wallet.balance > 0
        ) {
          setBalance(wallet.balance);
        } else {
          setBalance(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [canUseAccount, currentUser?.email, currentUser?.uid]);

  const safeMerchandise = roundCurrency(merchandiseSubtotal);

  const maxRedeem = useMemo(
    () =>
      calculateMaxCashbackRedeem({
        balance,
        redeemPercentPerOrder: config.redeemPercentPerOrder,
        merchandiseSubtotal: safeMerchandise,
      }),
    [balance, config.redeemPercentPerOrder, safeMerchandise],
  );

  const earnAmount = useMemo(
    () =>
      calculateCashbackEarnAmount({
        merchandiseSubtotal: safeMerchandise,
        earnPercent: config.earnPercent,
        enabled: config.enabled,
      }),
    [config.enabled, config.earnPercent, safeMerchandise],
  );

  return {
    loading,
    config,
    balance,
    maxRedeem,
    canRedeem: canUseAccount && maxRedeem > 0,
    accountEmail: currentUser?.email?.trim().toLowerCase() || null,
    earnAmount,
    earnPercent: config.enabled ? config.earnPercent : 0,
  };
};
